import { lazy, Suspense } from 'react';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { ProtectedRoute, AdminRoute } from './components/RouteGuards';
import PageLoader from './components/PageLoader';

// Layouts: livianos y compartidos, se cargan siempre.
import PublicLayout from './components/layout/PublicLayout';
import WholesaleLayout from './components/layout/WholesaleLayout';

// Páginas: carga diferida (code splitting). Cada una es su propio chunk
// y se descarga solo cuando el usuario navega a esa ruta.
const Home = lazy(() => import('./pages/public/Home'));
const Catalog = lazy(() => import('./pages/public/Catalog'));
const ProductDetail = lazy(() => import('./pages/public/ProductDetail'));
const Cart = lazy(() => import('./pages/public/Cart'));
const Checkout = lazy(() => import('./pages/public/Checkout'));
const PaymentResult = lazy(() => import('./pages/public/PaymentResult'));
const Wholesale = lazy(() => import('./pages/public/Wholesale'));
const WholesaleOrderPlaced = lazy(() => import('./pages/public/WholesaleOrderPlaced'));
const WholesaleRegister = lazy(() => import('./pages/auth/WholesaleRegister'));
const Page = lazy(() => import('./pages/public/Page'));

const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));

const AccountLayout = lazy(() => import('./pages/account/AccountLayout'));
const Profile = lazy(() => import('./pages/account/Profile'));
const Orders = lazy(() => import('./pages/account/Orders'));
const OrderDetail = lazy(() => import('./pages/account/OrderDetail'));
const Favorites = lazy(() => import('./pages/account/Favorites'));
const Addresses = lazy(() => import('./pages/account/Addresses'));

// Panel admin: el grupo más pesado (incluye Recharts). Diferido por completo,
// nunca se descarga para un visitante de la tienda.
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const ProductForm = lazy(() => import('./pages/admin/ProductForm'));
const AdminProductsOrder = lazy(() => import('./pages/admin/AdminProductsOrder'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderForm = lazy(() => import('./pages/admin/AdminOrderForm'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const AdminShipping = lazy(() => import('./pages/admin/AdminShipping'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminWholesalers = lazy(() => import('./pages/admin/AdminWholesalers'));
const AdminCustomerDetail = lazy(() => import('./pages/admin/AdminCustomerDetail'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminBanners = lazy(() => import('./pages/admin/AdminBanners'));
const AdminCarts = lazy(() => import('./pages/admin/AdminCarts'));
const AdminStaff = lazy(() => import('./pages/admin/AdminStaff'));
const AdminActivity = lazy(() => import('./pages/admin/AdminActivity'));

const NotFound = lazy(() => import('./pages/NotFound'));

// Rutas sin layout persistente propio: alcanza con un Suspense simple.
// (PublicLayout y AdminLayout envuelven su propio <Outlet/> en Suspense
// para no desmontar el navbar/sidebar cada vez que cambia la página.)
function SuspenseOutlet() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

export default function App() {
  return (
    <Routes>
      {/* ---------- PÚBLICO ---------- */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/catalogo" element={<Catalog />} />
        <Route path="/ofertas" element={<Catalog sale />} />
        <Route path="/categoria/:slug" element={<Catalog />} />
        {/* La tienda es solo catálogo: sin carrito, checkout ni pagos. */}
        <Route path="/producto/:slug" element={<ProductDetail />} />
        <Route path="/pagina/:slug" element={<Page />} />

        {/* ---------- COMPRA MINORISTA ---------- */}
        <Route path="/carrito" element={<Cart />} />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route path="/pago/:status" element={<PaymentResult />} />

        {/* ---------- CUENTA CLIENTE ---------- */}
        <Route
          path="/cuenta"
          element={
            <ProtectedRoute>
              <AccountLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Profile />} />
          <Route path="pedidos" element={<Orders />} />
          <Route path="pedidos/:id" element={<OrderDetail />} />
          <Route path="favoritos" element={<Favorites />} />
          <Route path="direcciones" element={<Addresses />} />
        </Route>
      </Route>

      {/* ---------- MAYORISTA ---------- */}
      {/* Sección aparte de la tienda: layout propio, sin las categorías arriba. */}
      <Route element={<WholesaleLayout />}>
        <Route path="/mayorista" element={<Wholesale />} />
        <Route path="/mayorista/ofertas" element={<Wholesale sale />} />
        <Route path="/mayorista/producto/:slug" element={<ProductDetail wholesale />} />
        <Route path="/mayorista/pedido/:id" element={<WholesaleOrderPlaced />} />
      </Route>

      <Route element={<SuspenseOutlet />}>

        {/* ---------- AUTH ---------- */}
        <Route path="/login" element={<Login />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/mayorista/registro" element={<WholesaleRegister />} />
        <Route path="/recuperar" element={<ForgotPassword />} />
        <Route path="/restablecer" element={<ResetPassword />} />

        {/* ---------- ADMIN ---------- */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="productos" element={<AdminProducts />} />
          <Route path="productos/ordenar" element={<AdminProductsOrder />} />
          <Route path="productos/nuevo" element={<ProductForm />} />
          <Route path="productos/:id" element={<ProductForm />} />
          <Route path="categorias" element={<AdminCategories />} />
          <Route path="contenido" element={<AdminContent />} />
          <Route path="diseno" element={<Navigate to="/admin/diseno/banners" replace />} />
          <Route path="diseno/banners" element={<AdminBanners />} />
          <Route path="pedidos" element={<AdminOrders />} />
          <Route path="pedidos/nuevo" element={<AdminOrderForm />} />
          <Route path="pedidos/:id" element={<AdminOrderDetail />} />
          <Route path="envios" element={<AdminShipping />} />
          <Route path="clientes" element={<AdminCustomers />} />
          <Route path="mayoristas" element={<AdminWholesalers />} />
          <Route path="clientes/:id" element={<AdminCustomerDetail />} />
          <Route path="promociones" element={<AdminCoupons />} />
          <Route path="pagos" element={<AdminPayments />} />
          <Route path="carritos" element={<AdminCarts />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="equipo" element={<AdminStaff />} />
          <Route path="actividad" element={<AdminActivity />} />
          <Route path="configuracion" element={<AdminSettings />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
